import type { DeliveryMethod } from '@/api/admin'

export type DeliveryMethodValue = {
  deliveryMethod: DeliveryMethod
  meetingUrl: string
  meetingInstructions: string
}

/**
 * In person / online toggle, with the joining link and instructions shown only when online.
 * The link is only ever emailed to people with a confirmed booking — never shown publicly.
 */
export function DeliveryMethodFields({
  value, onChange, venueField, disabled = false,
}: {
  value: DeliveryMethodValue
  onChange: (next: DeliveryMethodValue) => void
  /** The venue picker, rendered only when delivery is "In person". */
  venueField: React.ReactNode
  disabled?: boolean
}) {
  return (
    <div className="space-y-3">
      <div className="flex rounded-lg border border-gray-200 p-1 bg-gray-50 w-fit">
        {(['InPerson', 'Online'] as DeliveryMethod[]).map(m => (
          <button
            key={m}
            type="button"
            disabled={disabled}
            onClick={() => onChange({ ...value, deliveryMethod: m })}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 ${
              value.deliveryMethod === m ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {m === 'InPerson' ? 'In person' : 'Online'}
          </button>
        ))}
      </div>

      {value.deliveryMethod === 'InPerson' ? venueField : (
        <div className="space-y-2">
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-gray-700">Joining link</label>
            <input
              value={value.meetingUrl}
              onChange={e => onChange({ ...value, meetingUrl: e.target.value })}
              disabled={disabled}
              placeholder="https://zoom.us/j/…"
              className="w-full h-10 rounded-md border border-input bg-transparent px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-gray-700">Joining instructions (optional)</label>
            <textarea
              value={value.meetingInstructions}
              onChange={e => onChange({ ...value, meetingInstructions: e.target.value })}
              disabled={disabled}
              rows={2}
              placeholder="e.g. dial-in details, what to have ready…"
              className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          <p className="text-xs text-gray-400">
            Only sent to people with a confirmed booking, in their confirmation email — never shown on the public page.
          </p>
        </div>
      )}
    </div>
  )
}
